
import React, { useState } from "react";
import { FeedbackMessage as FeedbackMessageType } from "@/types/feedback";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { toast } from "sonner";
import { useFeedbackReactions } from "@/hooks/useFeedbackReactions";
import { useDeleteMessage } from "@/hooks/feedback/useDeleteMessage";
import { usePermissions } from "@/context/PermissionsContext";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import MessageHeader from "./message/MessageHeader";
import MessageContent from "./message/MessageContent";
import MessageActions from "./message/MessageActions";
import DeletedMessage from "./message/DeletedMessage";

interface FeedbackMessageProps {
  message: FeedbackMessageType;
  onReply: (parentId: string) => void;
  isReply?: boolean;
  replies?: FeedbackMessageType[];
}

const FeedbackMessage: React.FC<FeedbackMessageProps> = ({
  message,
  onReply,
  isReply = false,
  replies = [],
}) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { addReaction, removeReaction, hasUserReacted, getReactionCount } = useFeedbackReactions();
  const { deleteMessage } = useDeleteMessage();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const isOwnMessage = user?.id === message.user_id;
  const hasReacted = hasUserReacted(message.id, user?.id);
  const reactionCount = getReactionCount(message.id);
  const canDeleteMessages = hasPermission("feedback", "delete");

  const handleToggleReaction = () => {
    if (!user) {
      toast.error("Je moet ingelogd zijn om berichten te kunnen markeren");
      return;
    }

    if (hasReacted) {
      removeReaction.mutate({ message_id: message.id, user_id: user.id });
    } else {
      addReaction.mutate({
        message_id: message.id,
        user_id: user.id,
        username: user.username || user.name || "Gebruiker",
      });
    }
  };

  const handleDeleteMessage = () => {
    if (!canDeleteMessages) {
      toast.error("Je hebt geen rechten om berichten te verwijderen");
      return;
    }

    deleteMessage.mutate(message.id, {
      onSuccess: () => {
        setShowDeleteDialog(false);
        toast.success("Bericht succesvol verwijderd");
      }
    });
  };

  if (message.is_deleted) {
    return <DeletedMessage />;
  }

  return (
    <div className={`${isReply ? 'ml-8 mt-4' : 'mb-4'}`}>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <MessageHeader
              username={message.username}
              timestamp={message.timestamp}
              edited_at={message.edited_at}
              category_tag={!isReply ? message.category_tag : null}
            />

            {canDeleteMessages && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowDeleteDialog(true)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent>
          <MessageContent
            message={message.message}
            attachment_path={message.attachment_path}
          />
        </CardContent>

        <CardFooter className="p-0">
          <MessageActions
            isOwnMessage={isOwnMessage}
            hasReacted={hasReacted}
            reactionCount={reactionCount}
            onReply={() => onReply(message.id)}
            onToggleReaction={handleToggleReaction}
          />
        </CardFooter>
      </Card>

      {replies.length > 0 && (
        <div className="space-y-4">
          {replies.map((reply) => (
            <FeedbackMessage
              key={reply.id}
              message={reply}
              onReply={onReply}
              isReply={true}
              replies={[]}
            />
          ))}
        </div>
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bericht verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je dit bericht wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteMessage}
              className="bg-red-500 hover:bg-red-600 focus:ring-red-500"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FeedbackMessage;
